/**
 * Search everything from the top bar: reports, papers, people and vehicles/property in one results page.
 * A paper or case number that matches exactly opens straight away.
 */
import { useEffect } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDateTime } from '../shared/format.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, Empty, Flag, PageHeader, Spinner, StatusBadge, useLoad } from '../shared/ui.tsx';

const REPORT_STATUS: Record<string, [string, 'neutral' | 'info' | 'warn' | 'ok']> = {
  draft: ['Draft', 'neutral'],
  submitted: ['Awaiting approval', 'info'],
  returned: ['Returned', 'warn'],
  approved: ['Approved', 'ok'],
};
const fmtD = (d: string) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
/** Tag/VIN/serial-looking: one token of letters and digits with at least one digit. */
const looksLikeId = (q: string) => /^[A-Za-z0-9-]{3,20}$/.test(q.replace(/\s+/g, '')) && /\d/.test(q) && q.trim().split(/\s+/).length <= 2;

export function SearchPage() {
  const { path, navigate } = useRouter();
  const { can } = useAuth();
  const q = (new URLSearchParams(path.split('?')[1] ?? '').get('q') ?? '').trim();
  const rms = can('reports.read') || can('reports.approve');
  const civil = can('papers.read');
  const id = looksLikeId(q);
  const r = useLoad(
    async () => {
      if (q.length < 2) return null;
      const safe = <T,>(p: Promise<T>, empty: T) => p.catch(() => empty);
      const [reports, papers, people, vehicles] = await Promise.all([
        rms ? safe(api.get<any>(`/api/reports?tab=all&q=${encodeURIComponent(q)}`), { reports: [] }) : { reports: [] },
        civil ? safe(api.get<any>(`/api/papers?limit=25&tab=all&q=${encodeURIComponent(q)}`), { papers: [] }) : { papers: [] },
        civil || rms ? safe(api.get<any>(`/api/people?q=${encodeURIComponent(q)}`), { people: [] }) : { people: [] },
        id && (civil || rms) ? safe(api.get<any>(`/api/vehicles?q=${encodeURIComponent(q)}`), null) : null,
      ]);
      return { reports: reports.reports as any[], papers: papers.papers as any[], people: people.people as any[], vehicles };
    },
    [q],
  );
  // An exact case or paper number goes straight to it.
  useEffect(() => {
    const d = r.data;
    if (!d) return;
    const exactReport = d.reports.find((x) => x.case_number?.toLowerCase() === q.toLowerCase());
    const exactPaper = d.papers.find((x) => x.civilos_number?.toLowerCase() === q.toLowerCase());
    if (exactReport && !exactPaper) navigate(`/reports/${exactReport.id}`, true);
    else if (exactPaper && !exactReport) navigate(`/papers/${exactPaper.id}`, true);
  }, [r.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = r.data;
  const veh = d?.vehicles;
  const vehHits = veh ? veh.reports.length + veh.sightings.length + veh.noted.length : 0;
  const total = d ? d.reports.length + d.papers.length + d.people.length + vehHits : 0;
  return (
    <div className="stack">
      <PageHeader crumb="Search" title={<>Results for “{q}”</>} sub={d ? `${total} match${total === 1 ? '' : 'es'}` : undefined} />
      {r.loading ? (
        <Spinner />
      ) : !d ? (
        <Empty>Type at least 2 characters in the search box.</Empty>
      ) : total === 0 ? (
        <Empty>Nothing matches “{q}”.</Empty>
      ) : (
        <>
          {veh?.stolen?.map((s: any) => (
            <div key={s.id} className="alert bad veh-stolen" role="alert">
              <strong>STOLEN</strong> — {s.vehicle || 'Item'} {s.plate && <span className="mono">tag {s.plate}</span>} {s.vin && <span className="mono">VIN {s.vin}</span>} {s.serial && <span className="mono">serial {s.serial}</span>} ·{' '}
              <Link to={`/reports/${s.incidentId}`}>{s.caseNumber}</Link>
            </div>
          ))}
          {d.reports.length > 0 && (
            <Card title={`Incident reports (${d.reports.length})`}>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Case #</th>
                      <th>Occurred</th>
                      <th>Offense</th>
                      <th>Location</th>
                      <th>Officer</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.reports.slice(0, 25).map((x) => {
                      const [l, t] = REPORT_STATUS[x.status] ?? [x.status, 'neutral'];
                      return (
                        <tr key={x.id} className="clickable" onClick={() => navigate(`/reports/${x.id}`)}>
                          <td className="mono">{x.case_number}</td>
                          <td>{x.occurred_at ? fmtDateTime(x.occurred_at) : '—'}</td>
                          <td>{x.title || 'Incident'}</td>
                          <td>{x.location_text || '—'}</td>
                          <td>{x.author_name}</td>
                          <td>
                            <Badge tone={t} dot>
                              {l}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {d.people.length > 0 && (
            <Card title={`People (${d.people.length})`}>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>DOB</th>
                      <th>Record</th>
                      <th>Last address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.people.slice(0, 25).map((x) => (
                      <tr key={x.name_key} className="clickable" onClick={() => navigate(`/people/profile?key=${encodeURIComponent(x.name_key)}`)}>
                        <td>
                          <strong>{x.name}</strong>
                        </td>
                        <td>{x.dobs.length ? x.dobs.map(fmtD).join(', ') : '—'}</td>
                        <td>
                          <span className="row gap-sm wrap">
                            {x.arrests > 0 && <Flag tone="bad">Arrested {x.arrests}×</Flag>}
                            {x.reports > 0 && <Flag tone="info">{x.reports} report{x.reports === 1 ? '' : 's'}</Flag>}
                            {x.papers > 0 && <Flag>{x.papers} paper{x.papers === 1 ? '' : 's'}</Flag>}
                          </span>
                        </td>
                        <td>{x.last_address || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {d.papers.length > 0 && (
            <Card title={`Civil papers (${d.papers.length})`}>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Paper #</th>
                      <th>Defendant</th>
                      <th>Address</th>
                      <th>Case</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.papers.slice(0, 25).map((x) => (
                      <tr key={x.id} className="clickable" onClick={() => navigate(`/papers/${x.id}`)}>
                        <td className="mono">{x.civilos_number}</td>
                        <td>{x.defendant ?? '—'}</td>
                        <td>{x.address || '—'}</td>
                        <td className="mono">{x.case_number ?? '—'}</td>
                        <td>
                          <StatusBadge status={x.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {vehHits > 0 && (
            <Card title={`Vehicles & property (${vehHits})`} actions={<Link to={`/people?v=${encodeURIComponent(q)}`}>Full lookup</Link>}>
              <ul className="plain veh-list">
                {veh.reports.map((x: any, i: number) => (
                  <li key={`r${i}`}>
                    <Badge tone={x.loss === '7' ? 'bad' : x.loss === '5' ? 'ok' : 'neutral'}>{x.lossLabel}</Badge> <strong>{x.vehicle || x.type}</strong> {x.plate && <span className="mono">tag {x.plate}</span>} {x.vin && <span className="mono small">VIN {x.vin}</span>} {x.serial && <span className="mono small">serial {x.serial}</span>} · <Link to={`/reports/${x.incidentId}`}>{x.caseNumber}</Link>
                  </li>
                ))}
                {veh.sightings.map((x: any, i: number) => (
                  <li key={`s${i}`}>
                    <Badge>Seen at a door</Badge> <strong>{x.vehicle}</strong> · {x.where} · {fmtDateTime(x.at)}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
